// eslint-disable-next-line settle/preferred-modules
import { TextField as MuiTextField } from '@mui/material'
import { styled } from '@mui/material/styles'
import * as AutocompleteInputAdornmentStyled from 'settle-ui/components/Autocomplete/components/AutocompleteInputAdornment/AutocompleteInputAdornment.styled'
import { youShallNotPass } from 'lib/helpers/youShallNotPass/youShallNotPass'
import * as tkns from '../../tokens/common/Input'

export interface TextFieldStyledProps {
  warning?: boolean
  /**
   * Locks editing while keeping the field focusable, tabbable and copyable — unlike `disabled`.
   * Reach for it when the value is worth reading but the user may not change it.
   */
  readOnly?: boolean
}

export const TextField = styled(MuiTextField, {
  shouldForwardProp: youShallNotPass('warning', 'readOnly'),
})<TextFieldStyledProps>(({ theme, warning, error, size, readOnly }) => ({
  '& .MuiInputBase-multiline': {
    padding: 0,
    textAlign: 'start',
    // The input root is a flex container (`display: inline-flex; align-items: center` from MUI),
    // so applying the field's minHeight here lets it vertically center the textarea when it holds
    // a single line. The textarea itself stays content-sized (minHeight unset below) so it grows
    // from the centered position and switches to natural top alignment once content wraps.
    ...(size === 'small' && {
      minHeight: tkns.InputBaseSizeSmallMinHeight,
    }),
    ...(size === 'medium' && {
      minHeight: tkns.InputBaseSizeMediumMinHeight,
    }),
  },
  '& .MuiInputBase-input': {
    boxSizing: 'border-box',
    ...(size === 'small' && {
      padding: '4px 8px',
      minHeight: tkns.InputBaseSizeSmallMinHeight,
    }),
    ...(size === 'medium' && {
      padding: '8px 12px',
      minHeight: tkns.InputBaseSizeMediumMinHeight,
    }),
    // For multiline fields the minHeight lives on the flex-centering root (see above) instead.
    // Both the visible textarea and the hidden one TextareaAutosize uses to measure row height
    // must keep their natural (content) height — otherwise the row-height calc breaks and the
    // visible textarea can't be vertically centered when it only holds a single line.
    // https://github.com/mui/material-ui/blob/4cfd2f73c291df2a57fa5feb6bffdd39c544d838/packages/mui-material/src/TextareaAutosize/TextareaAutosize.tsx#L107
    '&.MuiInputBase-inputMultiline': {
      minHeight: 'unset',
    },
  },
  '& .MuiInputLabel-root': {
    ...(size === 'small' && {
      transform: 'translate(8px, 6px) scale(1)',
    }),
    ...(size === 'medium' && {
      transform: 'translate(12px, 10px) scale(1)',
    }),
  },
  '& .MuiInputLabel-root.MuiInputLabel-shrink': {
    ...(size === 'small' && {
      transform: 'translate(8px, -9px) scale(0.75)',
    }),
    ...(size === 'medium' && {
      transform: 'translate(12px, -9px) scale(0.75)',
    }),
  },
  '& .MuiOutlinedInput-notchedOutline': {
    ...(size === 'small' && {
      paddingLeft: '2px',
    }),
    ...(size === 'medium' && {
      paddingLeft: '6px',
    }),
  },
  ...(error && {
    '& .MuiOutlinedInput-root:not(.Mui-focused):not(.Mui-disabled)': {
      '& fieldset, &:hover fieldset': {
        borderColor: theme.palette.error.main,
      },
    },
  }),
  ...(warning &&
    !error && {
      '& .MuiOutlinedInput-root:not(.Mui-focused):not(.Mui-disabled)': {
        '& fieldset, &:hover fieldset': {
          borderColor: theme.tokens.ThemeColorCommonWarning,
        },
      },
      '& .MuiFormHelperText-root': {
        color: theme.tokens.ThemeColorCommonWarning,
      },
      '& label': {
        color: theme.tokens.ThemeColorCommonWarning,
      },
    }),
  ...(!readOnly && {
    '&:hover': {
      [`& ${AutocompleteInputAdornmentStyled.EditButton}`]: {
        display: 'flex',
      },
    },
  }),
}))
